import React, { useEffect, useState } from "react";
import API_BASE_URL from "../config";
import { useTheme } from "../context/ThemeContext";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SearchIcon from "@mui/icons-material/Search";

const TopProducts = () => {
    const { darkMode } = useTheme();
    const token = localStorage.getItem("token");

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    const [dateRange, setDateRange] = useState({ from: "", to: "" });

    const [sortConfig, setSortConfig] = useState({
        key: "quantity",
        direction: "desc",
    });

    const cardClass = darkMode
        ? "bg-gray-800 text-white border border-gray-700"
        : "bg-white text-black border border-gray-200";

    const fetchTopProducts = async () => {
        setLoading(true);

        const params = [];
        if (dateRange.from) params.push(`fromDate=${dateRange.from}`);
        if (dateRange.to) params.push(`toDate=${dateRange.to}`);

        const query = params.length ? "?" + params.join("&") : "";

        const res = await fetch(`${API_BASE_URL}/api/orders/stats/top-products${query}`, {
            headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
            const data = await res.json();
            setProducts(data);
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchTopProducts();
    }, []);

    const handleSort = (key) => {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
    };

    const filteredProducts = products.filter((p) =>
        p.productName.toLowerCase().includes(search.toLowerCase())
    );

    const sortedProducts = [...filteredProducts].sort((a, b) => {
        let valA, valB;

        switch (sortConfig.key) {
            case "productName":
                valA = a.productName.toLowerCase();
                valB = b.productName.toLowerCase();
                break;
            case "quantity":
                valA = a.totalQuantity;
                valB = b.totalQuantity;
                break;
            case "value":
                valA = a.totalSales;
                valB = b.totalSales;
                break;
            default:
                return 0;
        }

        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
    });

    const renderSortIcons = (column) => (
        <div className="flex flex-col items-center leading-none ml-1">
            <ArrowUpwardIcon
                fontSize="small"
                className={
                    sortConfig.key === column && sortConfig.direction === "asc"
                        ? ""
                        : "opacity-30"
                }
            />
            <ArrowDownwardIcon
                fontSize="small"
                className={
                    sortConfig.key === column && sortConfig.direction === "desc"
                        ? ""
                        : "opacity-30"
                }
            />
        </div>
    );

    return (
        <div className="w-full min-h-screen relative">

            {/* MODERN SCROLLBAR */}
            <style>
                {`
  .modern-scroll::-webkit-scrollbar { width: 8px; }
  .modern-scroll::-webkit-scrollbar-track { background: transparent; }
  .modern-scroll::-webkit-scrollbar-thumb {
    background: ${darkMode ? "#4b5563" : "#cfcfcf"};
    border-radius: 8px;
  }
  .modern-scroll::-webkit-scrollbar-thumb:hover {
    background: ${darkMode ? "#6b7280" : "#bfbfbf"};
  }

  /* DATE PICKER ICON FIX – Dark/Light mode */
  input[type="date"]::-webkit-calendar-picker-indicator {
    filter: ${darkMode ? "invert(1)" : "invert(0)"};
  }

  input[type="date"]::-moz-calendar-picker-indicator {
    filter: ${darkMode ? "invert(1)" : "invert(0)"};
  }
`}
            </style>

            <h1 className="text-3xl font-bold mb-8 tracking-wide">Top produse</h1>

            {/* FILTERS */}
            <div className="flex flex-wrap gap-4 mb-6 items-end">

                {/* Search bar */}
                <div className="relative w-72">
                    <SearchIcon
                        className={`absolute left-3 top-2.5 ${darkMode ? "text-gray-300" : "text-gray-600"
                            }`}
                    />
                    <input
                        type="text"
                        placeholder="Caută produs..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={`pl-10 w-full py-2 rounded-lg shadow-sm ${darkMode
                            ? "bg-gray-700 text-gray-100 border border-gray-600"
                            : "bg-gray-100 text-gray-800 border border-gray-300"
                            }`}
                    />
                </div>

                {/* From */}
                <div className="flex flex-col">
                    <label className="font-semibold mb-1">De la:</label>
                    <input
                        type="date"
                        value={dateRange.from}
                        onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                        className={`px-3 py-2 rounded-lg ${darkMode ? "bg-gray-700 text-gray-200" : "bg-gray-100"
                            }`}
                    />
                </div>

                {/* To */}
                <div className="flex flex-col">
                    <label className="font-semibold mb-1">Până la:</label>
                    <input
                        type="date"
                        value={dateRange.to}
                        onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                        className={`px-3 py-2 rounded-lg ${darkMode ? "bg-gray-700 text-gray-200" : "bg-gray-100"
                            }`}
                    />
                </div>

                {/* Button */}
                <button
                    onClick={fetchTopProducts}
                    className={`
    px-5 py-2.5 rounded-xl font-semibold flex items-center gap-2
    transition-all duration-200 shadow-md
    ${darkMode
                            ? "bg-red-600 hover:bg-red-500 text-white shadow-red-900/30"
                            : "bg-red-600 hover:bg-red-700 text-white shadow-red-300/30"
                        }
  `}
                >
                    <SearchIcon fontSize="small" />
                    Caută
                </button>

            </div>

            {/* TABLE WRAPPER (narrower) */}
            <div className={`p-6 rounded-2xl shadow-lg ${cardClass} max-w-[1600px]`}>

                {loading ? (
                    <p className="text-center text-lg">Se încarcă...</p>
                ) : (
                    <div className="max-h-[600px] overflow-y-auto rounded-2xl modern-scroll">
                        <table className="w-full text-sm table-fixed">
                            <colgroup>
                                <col className="w-[50%]" />
                                <col className="w-[20%]" />
                                <col className="w-[30%]" />
                            </colgroup>

                            <thead
                                className={`sticky top-0 z-10 ${darkMode ? "bg-gray-800" : "bg-white"
                                    }`}
                            >
                                <tr
                                    className={
                                        darkMode ? "border-b border-gray-600" : "border-b border-gray-200"
                                    }
                                >
                                    <th className="py-3 text-left cursor-pointer" onClick={() => handleSort("productName")}>
                                        <div className="flex items-center">Produs {renderSortIcons("productName")}</div>
                                    </th>

                                    <th className="py-3 text-left cursor-pointer" onClick={() => handleSort("quantity")}>
                                        <div className="flex items-center">Cantitate totală {renderSortIcons("quantity")}</div>
                                    </th>

                                    <th className="py-3 text-right cursor-pointer pr-4" onClick={() => handleSort("value")}>
                                        <div className="flex items-center justify-end">
                                            Valoare totală {renderSortIcons("value")}
                                        </div>
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {sortedProducts.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan="3"
                                            className="py-6 text-center text-gray-500 italic text-sm"
                                        >
                                            Nu există date în intervalul selectat.
                                        </td>
                                    </tr>
                                ) : (
                                    sortedProducts.map((p, idx) => (
                                        <tr
                                            key={p.productId}
                                            className={`border-b ${darkMode ? "border-gray-700" : "border-gray-100"
                                                } ${idx % 2 === 0
                                                    ? darkMode
                                                        ? "bg-gray-800/60"
                                                        : "bg-gray-50"
                                                    : ""
                                                }`}
                                        >
                                            <td className="py-3 font-medium">{p.productName}</td>
                                            <td className="py-3">
                                                {p.totalQuantity}{" "}
                                                <span className="text-xs opacity-70">{p.unitType}</span>
                                            </td>
                                            <td className="py-3 text-right pr-4">{p.totalSales.toFixed(2)} EUR</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>

                        </table>
                    </div>

                )}
            </div>
        </div>
    );
};

export default TopProducts;
